import { useForm } from "@tanstack/react-form";
import { useTranslation } from "next-i18next";
import { MouseEvent } from "react";
import { toast } from "react-toastify";

import { InputAdornment, TextField, useTheme } from "@mui/material";

import MailLine from "@public/contact/mail-line.svg";
import MailLinkDark from "@public/contact/mail_link_dark.svg";
import MailLinkLight from "@public/contact/mail_link_light.svg";
import PhoneLine from "@public/contact/phone-line.svg";
import { space } from "@utils";
import { trpc } from "@utils/trpc";
import { notEmptyStringSchema } from "@utils/zod";

import {
  AlternativeMessageContainer,
  ContactFormButton,
  ContactFormCard,
  ContactFormMail,
  ContactFormPolicyLink,
  ContactFormText,
  ContactFormTitle,
  SendMessageContainer,
} from "./styles.styled";
import { TextFieldWithCounter } from "./TextFieldWithCounter";

export const ContactForm = trpc.withTRPC(() => {
  const { t } = useTranslation();
  const {
    palette: { mode },
  } = useTheme();
  const { mutateAsync, isPending } = trpc.emailRouter.sendEmail.useMutation();

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      email: "",
      phone: "",
      topic: "",
    },
    onSubmit: async ({ value: { email, phone, topic } }) => {
      try {
        await mutateAsync({ email, phone, topic });
        toast.success(t("contact-page.toast-success"));
      } catch (e) {
        toast.warn(t("contact-page.toast-error"));
        console.log(e);
      }
    },
  });

  const contactMail = "hello@bluesbrackets.com";
  const copyToClipboard = (
    e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
  ) => {
    e.preventDefault();
    navigator.clipboard.writeText(contactMail);
    toast.success(t("common.copied_to_clipboard"));
  };

  return (
    <ContactFormCard>
      <ContactFormTitle>{t("contact-page.header")}</ContactFormTitle>

      <div className="flex flex-col gap-4">
        <div className="flex gap-4 max-tablet:flex-col">
          <Field
            name="email"
            validators={{
              onSubmit: ({ value }) =>
                notEmptyStringSchema
                  .email({
                    message: "common.validation.email",
                  })
                  .safeParse(value).error?.issues[0].message,
            }}
          >
            {({
              state: {
                value,
                meta: { errors },
              },
              handleChange,
              handleBlur,
            }) => (
              <TextField
                className="grow"
                value={value}
                onChange={e => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder={t("contact-page.mail")}
                error={!!errors.length}
                helperText={
                  errors.length ? t(errors[0]?.toString() ?? "") : undefined
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailLine />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Field>

          <Field name="phone">
            {({
              state: {
                value,
                meta: { errors },
              },
              handleChange,
              handleBlur,
            }) => (
              <TextField
                className="grow"
                value={value}
                onChange={e => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder={t("contact-page.phone")}
                error={!!errors.length}
                helperText={
                  errors.length ? t(errors[0]?.toString() ?? "") : undefined
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneLine />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Field>
        </div>

        <Field
          name="topic"
          validators={{
            onSubmit: ({ value }) =>
              notEmptyStringSchema.safeParse(value).error?.issues[0].message,
          }}
        >
          {({
            state: {
              value,
              meta: { errors },
            },
            handleChange,
            handleBlur,
          }) => (
            <TextFieldWithCounter
              maxLength={2000}
              value={value}
              onChange={e => handleChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={`${t("contact-page.topic")}...`}
              multiline
              error={!!errors.length}
              helperText={
                errors.length ? t(errors[0]?.toString() ?? "") : undefined
              }
            />
          )}
        </Field>
      </div>

      <div className="flex flex-col gap-2 tablet:gap-4">
        <SendMessageContainer>
          <ContactFormButton
            onClick={handleSubmit}
            disabled={isPending}
            className="mobile:max-tablet:w-full"
          >
            {t("contact-page.send")}
          </ContactFormButton>

          <AlternativeMessageContainer>
            <ContactFormText>{`${t("contact-page.write-directly")}:`}</ContactFormText>

            <div
              className="flex gap-1 cursor-pointer"
              onClick={e => copyToClipboard(e)}
            >
              <ContactFormMail>{contactMail}</ContactFormMail>

              {mode === "light" ? <MailLinkLight /> : <MailLinkDark />}
            </div>
          </AlternativeMessageContainer>
        </SendMessageContainer>

        <ContactFormText>
          {t("contact-page.acceptance")}

          {space}

          <ContactFormPolicyLink href="/policy">
            {t("contact-page.policy")}
          </ContactFormPolicyLink>
        </ContactFormText>
      </div>
    </ContactFormCard>
  );
});
